// What the organisation's audit log is filtered by (spec/13 §15, spec/07 §11.2): the actor, the
// action (exact, or a prefix `sessions.*`), the target, the venue, a break-glass grant, a request id
// and a date window. There is no organisation filter — the screen is one organisation's own log, named
// in the path — and no reason: a reason is asked of an HQ reader, who reads across organisations
// (spec/07 §11), never of an owner reading their own.
//
// **The window is two instants, half-open** (`from` inclusive, `to` exclusive), on `occurredAt`. An
// organisation's log spans venues that may keep different time zones, so a quick range — Today,
// Yesterday, the last 7 or 30 days — is turned into the two instants that bound it in a reference
// zone: the venue's, when one venue is filtered, and otherwise the reader's own. A custom window is
// written in UTC, unambiguous for a log that no single venue's clock is right for.
//
// The pure, i18n-free leaves here (id shortening, the request-id test, the UTC instant maths) mirror
// the platform log's own (`hq/audit/filters.ts`), kept apart so the organisation console's build
// carries none of `src/hq/` (`__tests__/builds.test.ts`).

import type { ListOrganizationAuditQuery } from '@clavian/api-client';
import { isTypeId } from '@clavian/contracts/typeid';

/** A request id (`req_…`), which is also its trace id (spec/08 §2.11). */
export const isRequestId = (value: string): boolean => isTypeId(value, 'req');

/** The form's `datetime-local` value, read as UTC, as the instant the api takes. */
export function instantFromUtcInput(value: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) return undefined;
  const instant = new Date(`${value.length === 16 ? `${value}:00` : value}.000Z`);
  return Number.isNaN(instant.getTime()) ? undefined : instant.toISOString();
}

/** An instant, back into the form's `datetime-local` value, in UTC. */
export const utcInputFromInstant = (instant: string): string => instant.slice(0, 16);

/** `attachment; filename="audit-…csv"` → the name, or `undefined`. */
export function fileNameOf(disposition: string | null): string | undefined {
  const name = disposition === null ? undefined : /filename="([^"/\\]+)"/.exec(disposition)?.[1];
  return name === undefined || name === '' ? undefined : name;
}

/**
 * An id, short enough for a table cell: a TypeID keeps its prefix and its last six characters
 * (`ven_…pf2dc`), a uuid its first eight. The full id is always one click away.
 */
export function shortId(id: string): string {
  const typeId = /^([a-z][a-z_]*)_([0-9a-z]{26})$/.exec(id);
  if (typeId !== null) return `${typeId[1]}_…${(typeId[2] ?? '').slice(-6)}`;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id)) return id.slice(0, 8);
  return id.length > 14 ? `${id.slice(0, 12)}…` : id;
}

/** The filters this screen sends. No `organizationId` (the path scopes it) and no `reason`. */
export const AUDIT_FILTER_KEYS = ['venueId', 'action', 'actorKind', 'actorId', 'targetType', 'targetId', 'onBehalfOf', 'requestId', 'from', 'to'] as const;

export type AuditFilterKey = (typeof AUDIT_FILTER_KEYS)[number];

/** A filter as applied: only the keys that were given, each a string the api takes. */
export type AuditFilter = Readonly<Partial<Record<AuditFilterKey, string>>>;

/** `AuditActorSchema.kind`. */
export const AUDIT_ACTOR_KINDS = ['user', 'member', 'device', 'api_key', 'system'] as const;

/** The most rows one export carries (`AUDIT_EXPORT_MAX_ROWS`): past it the api refuses with `422`. */
export const EXPORT_MAX_ROWS = 10_000;

/** The filter as the list's query (and the export's body) take it. */
export function auditQuery(filter: AuditFilter): Omit<ListOrganizationAuditQuery, 'cursor' | 'limit'> {
  return filter as Omit<ListOrganizationAuditQuery, 'cursor' | 'limit'>;
}

export type FilterForm = Readonly<Record<AuditFilterKey, string>>;

export const EMPTY_FORM: FilterForm = Object.fromEntries(AUDIT_FILTER_KEYS.map((key) => [key, ''])) as FilterForm;

export function formFromFilter(filter: AuditFilter): FilterForm {
  return {
    ...EMPTY_FORM,
    ...filter,
    from: filter.from === undefined ? '' : utcInputFromInstant(filter.from),
    to: filter.to === undefined ? '' : utcInputFromInstant(filter.to),
  };
}

export type FilterProblem = 'venue' | 'from' | 'to' | 'window';

/** The form, checked for what is plainly not an id or an instant, and made the filter it asks for. */
export function filterFromForm(form: FilterForm): { filter: AuditFilter; problems: Partial<Record<AuditFilterKey, FilterProblem>> } {
  const problems: Partial<Record<AuditFilterKey, FilterProblem>> = {};
  const filter: Partial<Record<AuditFilterKey, string>> = {};
  for (const key of AUDIT_FILTER_KEYS) {
    const value = form[key].trim();
    if (value === '') continue;
    if (key === 'from' || key === 'to') {
      const instant = instantFromUtcInput(value);
      if (instant === undefined) problems[key] = key;
      else filter[key] = instant;
      continue;
    }
    filter[key] = key === 'venueId' || key === 'requestId' ? value.toLowerCase() : value;
  }
  if (filter.venueId !== undefined && !isTypeId(filter.venueId, 'ven')) problems.venueId = 'venue';
  if (filter.from !== undefined && filter.to !== undefined && Date.parse(filter.from) >= Date.parse(filter.to)) problems.to = 'window';
  return { filter, problems };
}

/** What the api would call the file, for when its header cannot be read (another origin that hides it). */
export const fallbackFileName = (organizationId: string, at: Date = new Date()): string => `audit-${organizationId}-${at.toISOString().slice(0, 10)}.csv`;

/** The quick time ranges: `all` sends no window; `custom` is the form's; the rest are calendar days. */
export const AUDIT_RANGES = ['today', 'yesterday', 'last7', 'last30', 'all', 'custom'] as const;
export type AuditRange = (typeof AUDIT_RANGES)[number];

/** The reader's own time zone, as the browser reports it — the reference zone when no one venue is filtered. */
export const readerTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// The calendar-day maths runs on `Intl` alone, so the organisation console's build stays free of
// `@clavian/domain` — whose barrel would drag a zod validator that evaluates code, which the console's
// content-security policy forbids (`__tests__/pages-csp.test.ts`; see MEMORY on eval-free imports).

interface DateParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** The wall-clock parts of `instant` in `zone` — the year, month and day, and the hour/minute/second. */
function zonedParts(instant: number, zone: string): DateParts & { readonly hour: number; readonly minute: number; readonly second: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instant));
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((part) => part.type === type)?.value ?? '0');
  const hour = get('hour') % 24; // some engines emit 24 for midnight
  return { year: get('year'), month: get('month'), day: get('day'), hour, minute: get('minute'), second: get('second') };
}

/** The zone's offset (ms) at `instant`: the wall clock read as UTC, less the instant. */
function zoneOffset(instant: number, zone: string): number {
  const parts = zonedParts(instant, zone);
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - instant;
}

/** The instant at which `zone`'s wall clock reads midnight on the given date (DST-safe, two passes). */
function zonedDayStart(date: DateParts, zone: string): number {
  const guess = Date.UTC(date.year, date.month - 1, date.day, 0, 0, 0);
  const first = guess - zoneOffset(guess, zone);
  const second = guess - zoneOffset(first, zone);
  return second;
}

/** The calendar date `days` on from another, as UTC-date arithmetic (independent of any zone's DST). */
function shiftDate(date: DateParts, days: number): DateParts {
  const at = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: at.getUTCFullYear(), month: at.getUTCMonth() + 1, day: at.getUTCDate() };
}

/** The `[from, to)` instants a quick range means, computed as calendar days in `zone` (DST-safe). */
export function rangeWindow(range: AuditRange, zone: string, now: number = Date.now()): { from?: string; to?: string } {
  if (range === 'all' || range === 'custom') return {};
  const today = zonedParts(now, zone);
  const startOf = (date: DateParts): string => new Date(zonedDayStart(date, zone)).toISOString();
  const tomorrow = shiftDate(today, 1);
  switch (range) {
    case 'today':
      return { from: startOf(today), to: startOf(tomorrow) };
    case 'yesterday':
      return { from: startOf(shiftDate(today, -1)), to: startOf(today) };
    case 'last7':
      return { from: startOf(shiftDate(today, -6)), to: startOf(tomorrow) };
    case 'last30':
      return { from: startOf(shiftDate(today, -29)), to: startOf(tomorrow) };
  }
}

/** The filter with a quick range's window in place of any it had. */
export function withRange(filter: AuditFilter, range: AuditRange, zone: string, now: number = Date.now()): AuditFilter {
  return { ...without(filter, 'from', 'to'), ...rangeWindow(range, zone, now) };
}

/** The filter without `keys`. */
export function without(filter: AuditFilter, ...keys: readonly AuditFilterKey[]): AuditFilter {
  return Object.fromEntries(Object.entries(filter).filter(([key]) => !keys.includes(key as AuditFilterKey))) as AuditFilter;
}

/** The fields "More filters" holds, for its count. Target is fixed on a player's tab, so it is left out there. */
export const MORE_FILTER_KEYS = ['venueId', 'action', 'actorId', 'targetType', 'targetId', 'onBehalfOf', 'from', 'to'] as const satisfies readonly AuditFilterKey[];

/** `action`: an exact action, or a prefix written `sessions.*`. */
const ACTION_FILTER = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*(?:\.\*)?$/;

export type SearchTerm = { readonly key: 'requestId' | 'venueId' | 'action'; readonly value: string } | undefined;

/**
 * What the search box asks for, or `undefined` for words it cannot read. `actionsByLabel` maps an
 * action's copy, lower-cased, to its code. A request id traces; a `ven_` id filters by venue;
 * anything else is an action — its copy, its code, or a module read as `module.*`.
 */
export function parseSearch(text: string, actionsByLabel: ReadonlyMap<string, string> = new Map()): SearchTerm {
  const value = text.trim().toLowerCase();
  if (value === '') return undefined;
  if (isRequestId(value)) return { key: 'requestId', value };
  if (isTypeId(value, 'ven')) return { key: 'venueId', value };
  const labelled = actionsByLabel.get(value.replace(/\s+/g, ' '));
  if (labelled !== undefined) return { key: 'action', value: labelled };
  if (!ACTION_FILTER.test(value)) return undefined;
  return { key: 'action', value: value.includes('.') ? value : `${value}.*` };
}
